import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import LandingPage from './components/LandingPage';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import ProfileSetup from './components/ProfileSetup';
import FeedbackForm from './components/FeedbackForm';
import Settings from './components/Settings';
import Pricing from './components/Pricing';
import Features from './components/Features';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookieConsent from './components/CookieConsent';
import { VersionChecker } from './components/VersionChecker';
import { FounderDashboard } from './components/FounderDashboard';
import { useState, useEffect } from 'react';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8CAFA8] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function AuthPage() {
  const { user, loading, profileLoading, isProfileComplete } = useAuth();

  console.log('[AuthPage] State:', {
    user: !!user,
    loading,
    profileLoading,
    isProfileComplete,
    timestamp: new Date().toISOString()
  });

  // CRITICAL: Wait for BOTH auth AND profile to finish loading
  // Do not make ANY redirect decisions until both are complete
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8CAFA8] border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // User is authenticated, check profile completion with fresh data
  if (user) {
    if (!isProfileComplete) {
      console.log('[AuthPage] ❌ Profile incomplete - redirecting to profile setup');
      return <Navigate to="/profile" replace />;
    }
    console.log('[AuthPage] ✓ Profile complete - redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  // Not authenticated - show login form
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-6">
      <AuthForm />
    </div>
  );
}

function HomePage() {
  const [showAuth, setShowAuth] = useState(false);
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8CAFA8] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (showAuth) {
    return <Navigate to="/auth" replace />;
  }

  return <LandingPage onGetStarted={() => setShowAuth(true)} />;
}

function App() {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        if (registrations.length > 0) {
          console.log('[App] Unregistering old service workers:', registrations.length);
          registrations.forEach(reg => reg.unregister());
        }
      });
    }
  }, []);

  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <CookieConsent />
          <VersionChecker />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-6">
                    <ProfileSetup />
                  </div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/founder/dashboard"
              element={
                <ProtectedRoute>
                  <FounderDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/feedback/:linkId" element={<FeedbackForm />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
